import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Loader2, MapPin, Sun, Sunset, Moon, Lightbulb, ArrowRight, Wand2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { inr } from "@/lib/format";
import { toast } from "sonner";
import Shell from "@/components/Shell";

const VIBES = ["Spiritual & offbeat", "Adventure & adrenaline", "Hidden places", "Beaches & chill", "Mountains & treks", "Party & nightlife"];

export default function TripPlanner() {
  const [form, setForm] = useState({ days: 4, budget: 30000, vibe: "", region: "", travellers: 2 });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/ai/plan", form);
      setResult(r.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  const expById = (id) => (result?.experiences || []).find((e) => e.id === id);

  return (
    <Shell>
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-14 pb-8">
        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-accent"><Sparkles size={14} /> AI Trip Planner · Gemini</span>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight mt-3 text-foreground">Plan my trip</h1>
        <p className="text-muted-foreground mt-4 max-w-lg text-lg">Tell us the shape of your journey — our AI drafts a day-by-day India itinerary with real experiences you can book.</p>

        <div className="mt-8 rounded-2xl border border-primary/10 bg-card p-6" data-testid="planner-form">
          <div className="grid sm:grid-cols-4 gap-4">
            <label className="block"><span className="text-sm font-medium text-foreground/80 mb-1 block">Days</span>
              <input type="number" min={1} max={21} value={form.days} onChange={(e) => setForm({ ...form, days: Math.max(1, Number(e.target.value)) })} data-testid="plan-days"
                className="w-full h-11 rounded-lg border border-input bg-transparent px-3 focus:ring-2 focus:ring-accent focus:outline-none" /></label>
            <label className="block"><span className="text-sm font-medium text-foreground/80 mb-1 block">Travellers</span>
              <input type="number" min={1} max={12} value={form.travellers} onChange={(e) => setForm({ ...form, travellers: Math.max(1, Number(e.target.value)) })} data-testid="plan-travellers"
                className="w-full h-11 rounded-lg border border-input bg-transparent px-3 focus:ring-2 focus:ring-accent focus:outline-none" /></label>
            <label className="block"><span className="text-sm font-medium text-foreground/80 mb-1 block">Budget (₹, total)</span>
              <input type="number" min={0} step={1000} value={form.budget} onChange={(e) => setForm({ ...form, budget: Number(e.target.value) })} data-testid="plan-budget"
                className="w-full h-11 rounded-lg border border-input bg-transparent px-3 focus:ring-2 focus:ring-accent focus:outline-none" /></label>
            <label className="block"><span className="text-sm font-medium text-foreground/80 mb-1 block">Region (optional)</span>
              <input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="Himachal, Goa…" data-testid="plan-region"
                className="w-full h-11 rounded-lg border border-input bg-transparent px-3 focus:ring-2 focus:ring-accent focus:outline-none" /></label>
          </div>
          <div className="mt-4">
            <span className="text-sm font-medium text-foreground/80 mb-2 block">Vibe</span>
            <div className="flex flex-wrap gap-2">
              {VIBES.map((v) => (
                <button key={v} onClick={() => setForm({ ...form, vibe: v })} data-testid={`vibe-${v.split(" ")[0].toLowerCase()}`}
                  className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${form.vibe === v ? "bg-primary text-primary-foreground border-primary" : "border-primary/20 text-foreground/70 hover:bg-secondary"}`}>{v}</button>
              ))}
            </div>
          </div>
          <button onClick={generate} disabled={loading} data-testid="plan-generate"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-7 py-3.5 font-semibold disabled:opacity-60 hover:-translate-y-px transition-transform">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Wand2 size={18} />} {loading ? "Crafting your journey…" : "Generate itinerary"}
          </button>
        </div>
      </section>

      {result && (
        <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-24" data-testid="planner-result">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-primary text-primary-foreground p-8">
            <h2 className="font-display text-3xl sm:text-4xl">{result.plan.title}</h2>
            <p className="text-primary-foreground/80 mt-2">{result.plan.summary}</p>
            <p className="mt-4 text-sm">Estimated total: <b className="font-display text-xl">{inr(result.plan.total_est_cost)}</b></p>
          </motion.div>

          <div className="mt-6 space-y-4">
            {result.plan.days.map((d, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="rounded-2xl border border-primary/10 bg-card p-6" data-testid={`plan-day-${d.day}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-semibold text-xl text-foreground"><span className="text-accent font-display mr-2">Day {d.day}</span>{d.theme}</h3>
                  {d.est_cost ? <span className="text-sm text-muted-foreground">≈ {inr(d.est_cost)}</span> : null}
                </div>
                <div className="grid sm:grid-cols-3 gap-4 mt-4 text-sm">
                  <div><span className="flex items-center gap-1.5 text-accent font-semibold mb-1"><Sun size={14} /> Morning</span><p className="text-foreground/80">{d.morning}</p></div>
                  <div><span className="flex items-center gap-1.5 text-accent font-semibold mb-1"><Sunset size={14} /> Afternoon</span><p className="text-foreground/80">{d.afternoon}</p></div>
                  <div><span className="flex items-center gap-1.5 text-accent font-semibold mb-1"><Moon size={14} /> Evening</span><p className="text-foreground/80">{d.evening}</p></div>
                </div>
                {(d.experience_ids || []).map((id) => expById(id)).filter(Boolean).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-primary/10 flex flex-wrap gap-3">
                    {(d.experience_ids || []).map((id) => expById(id)).filter(Boolean).map((e) => (
                      <Link key={e.id} to={`/experience/${e.id}`} className="group flex items-center gap-3 rounded-xl border border-primary/10 p-2 pr-4 hover:bg-secondary transition-colors" data-testid={`plan-exp-${e.id}`}>
                        <img src={e.image} alt={e.title} className="w-12 h-12 rounded-lg object-cover" />
                        <span><span className="block text-sm font-semibold text-foreground">{e.title}</span><span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={11} /> {e.location} · {inr(e.price)}</span></span>
                        <ArrowRight size={15} className="text-accent group-hover:translate-x-0.5 transition-transform" />
                      </Link>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {result.plan.tips?.length > 0 && (
            <div className="mt-6 rounded-2xl border border-accent/30 bg-accent/5 p-6">
              <h3 className="font-semibold text-foreground flex items-center gap-2 mb-3"><Lightbulb size={18} className="text-accent" /> Good to know</h3>
              <ul className="space-y-1.5 text-sm text-foreground/80 list-disc pl-5">
                {result.plan.tips.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-4">AI-generated with Gemini. Prices are estimates — confirm on each experience page.</p>
        </section>
      )}
    </Shell>
  );
}
